/**
 * Circle element renderer
 * Extracted from Canvas.jsx to reduce file size
 */
import React from "react";
import { flushSync } from "react-dom";
import CircleElement from "../../circle/CircleElement";
import { getOffCanvasStageWidthPx } from "../../../utils/mainCanvasLayout";
import { isElementInViewport } from "../../../utils/viewportCullingUtils";
import { isPrimaryMouseButton } from "../../../utils/pointerButtonUtils";
import { constrainOffCanvasLiveTransformAttrs } from "../../../utils/offCanvasTransformUtils";

/**
 * Creates a circle element renderer factory function
 */
export const createCircleRenderer = ({
  circles,
  offCanvasCircles,
  setCircles,
  setOffCanvasCircles,
  selectedObject,
  selectedObjects = [], // Add selectedObjects parameter
  strokeColor,
  images,
  rectangles = [], // Add for cross-canvas check
  texts = [], // Add for cross-canvas check
  arrows = [], // Add for cross-canvas check
  lines = [], // Add for cross-canvas check
  doubleSidedArrows = [], // Add for cross-canvas check
  canvasHeight,
  canvasResizeTrigger,
  effectiveZoomLevel,
  disableInteraction,
  debouncedStateUpdate,
  isTransformingRef,
  setSelectedObject,
  setSelectedObjects,
  setShowTextBox,
  setSelectedText,
  setSelectedImage,
  setSelectedArrow, // Add setSelectedArrow to clear arrow selections
  setSelectedDoubleSidedArrow, // Add setSelectedDoubleSidedArrow to clear double-sided arrow selections
  setSelectedLine, // Add setSelectedLine to clear line selections
  onClick,
  handleElementDragEnd,
  saveToHistory,
  handleDragPreview,
  clearDragPreview,
  handleElementCopy,
  lockedAction,
  isCloneMode = false,
  isElementBeingEdited = false,
  setPendingChanges = () => { },
  pendingChangesRef = null, // Ref to update synchronously
  isMultiSelectMode = false, // Multi-select mode flag
  handleGroupDragStart = null, // Handler for group drag start
  handleGroupDragMove = null, // Handler for group drag move
  cancelActiveGroupDrag = null,
  stageRef = null, // Main canvas stage ref
  offCanvasStageRef = null, // Off-canvas stage ref
  justFinishedTransformRef = null, // Ref to skip clearing selection on next stage click after transform end
  lastTransformStartTimeRef = null, // Time-based fallback for transform-end click skip
  selectionBoxRef = null,
  selectionBoxStageRef = null,
  blockClicksOnOffCanvasElements = false,
  blockClicksOnMainStageElements = false,
  clearDebouncedTransformForKey = null,
  crossStageGestureBegin = null,
  crossStageGestureEnd = null,
}) => {
  return ({
    circle,
    isOffCanvas = false,
    sourceStageRef,
    isPreview = false,
    opacity = undefined, // Opacity override for dimming non-selected elements
  }) => {
    const setCircleState = isOffCanvas ? setOffCanvasCircles : setCircles;
    const isInteractive = !isPreview;
    const isSelected =
      isInteractive &&
      circle.id === selectedObject?.id &&
      selectedObject?.type === "Circle";

    // Check if element is in selectedObjects array (for multi-selection)
    const isInSelectedObjects = selectedObjects && selectedObjects.some(
      (obj) => obj.id === circle.id && obj.type === "Circle"
    );

    // When duplicate-to-selected-location is on, no drag (single or group). Otherwise draggable if selected or in multi-selection.
    const isDraggableElement = !isCloneMode && !lockedAction && (isSelected || (isInSelectedObjects && selectedObjects.length > 1));

    // Use provided opacity or element's own opacity, defaulting to 1
    const elementOpacity = opacity !== undefined ? opacity : (circle.opacity !== undefined ? circle.opacity : 1);

    // Get current canvas dimensions (off-canvas stage is full triple-column width, not main width)
    const stageWidth = isOffCanvas
      ? sourceStageRef?.width() || getOffCanvasStageWidthPx()
      : sourceStageRef?.width() || window.innerWidth * 0.45;
    const stageHeight =
      sourceStageRef?.height() ||
      (parseInt(canvasHeight) / 100) * window.innerHeight;

    // Viewport culling: skip rendering circles far outside visible area
    const shouldAlwaysRender = isPreview || isSelected || isInSelectedObjects;
    if (!shouldAlwaysRender) {
      const stage = isOffCanvas ? offCanvasStageRef?.current : stageRef?.current;
      if (stage && !isElementInViewport(circle, "Circle", stage)) {
        return null;
      }
    }

    return (
      <CircleElement
        key={isPreview ? `${circle.id}-preview` : circle.id}
        circle={{ ...circle, opacity: elementOpacity }}
        isDraggable={isInteractive && !disableInteraction && isDraggableElement}
        disableInteraction={disableInteraction || !isInteractive}
        isSelected={isSelected}
        isElementBeingEdited={isElementBeingEdited}
        selectedObject={selectedObject}
        selectedObjects={selectedObjects}
        crossStageGestureBegin={crossStageGestureBegin}
        crossStageGestureEnd={crossStageGestureEnd}
        images={images}
        stageRef={sourceStageRef}
        effectiveZoomLevel={effectiveZoomLevel}
        onImageClick={(image) => {
          if (!isInteractive) {
            return;
          }
          setSelectedImage(image);
          setSelectedObject({ ...image, type: "Image" });
          setSelectedObjects([{ ...image, type: "Image" }]);
          setShowTextBox(false);
          setSelectedText(null);
        }}
        onDragMove={
          isInteractive
            ? (newAttrs, event) => {
              // If multiple elements are selected AND this element is part of the selection,
              // use group drag handler instead of individual drag
              const isInSelectedObjects = selectedObjects?.some(
                (obj) => obj.id === newAttrs.id && obj.type === "Circle"
              );
              if (selectedObjects && selectedObjects.length > 1 && isInSelectedObjects) {
                // Capture initial positions on the first call (pre-drag, from
                // CircleElement.onDragStart). handleGroupDragStart now sets
                // isGroupDraggingRef.current = true immediately, so any subsequent call
                // from the first real onDragMove is a safe no-op.
                handleGroupDragStart?.(newAttrs.id, "Circle");
                // Use group drag handler - pass event for accurate boundary detection
                handleGroupDragMove?.(newAttrs.id, "Circle", newAttrs.x, newAttrs.y, event);

                // CRITICAL: Don't update selectedObject during group drag to prevent state conflict
                // The group drag handler updates selectedObjects (plural) which is what matters for the group
                return;
              }

              // CRITICAL FIX: Do NOT transfer state arrays during drag move.
              // Moving the element between arrays/stages mid-drag can interrupt Konva dragging and fire dragEnd immediately.
              // Instead: determine target canvas based on mouse position and convert coordinates accordingly.
              let displayAttrs = { ...newAttrs };

              // Determine target canvas based on mouse position (like group drag handler)
              let targetCanvas = null;
              if (event?.evt && sourceStageRef && offCanvasStageRef?.current) {
                try {
                  const clientX = event.evt.clientX;
                  const clientY = event.evt.clientY;

                  // CRITICAL FIX: Use stageRef?.current for main bounding box
                  // not sourceStageRef which might be different
                  const mainStage = stageRef?.current;
                  const offCanvasStage = offCanvasStageRef.current;
                  const mainBBox = mainStage?.container?.()?.getBoundingClientRect?.();
                  const offBBox = offCanvasStage?.container?.()?.getBoundingClientRect?.();

                  if (mainBBox && offBBox) {
                    const withinMain =
                      clientX >= mainBBox.left &&
                      clientX <= mainBBox.right &&
                      clientY >= mainBBox.top &&
                      clientY <= mainBBox.bottom;

                    const withinOff =
                      clientX >= offBBox.left &&
                      clientX <= offBBox.right &&
                      clientY >= offBBox.top &&
                      clientY <= offBBox.bottom;

                    targetCanvas = withinMain ? "main" : withinOff ? "offCanvas" : null;

                    // CRITICAL: If dragging over off-canvas area but element is on main canvas stage,
                    // convert the position to be relative to off-canvas stage origin
                    if (targetCanvas === "offCanvas" && !isOffCanvas && mainBBox && offBBox) {
                      // Element is on main canvas stage but being dragged over off-canvas area
                      // Convert position from main canvas stage coordinates to off-canvas stage coordinates

                      // Step 1: Convert from main canvas stage coordinates to absolute/client coordinates
                      const absX = mainBBox.left + newAttrs.x;
                      const absY = mainBBox.top + newAttrs.y;

                      // Step 2: Convert from absolute/client coordinates to off-canvas stage coordinates
                      const convertedX = absX - offBBox.left;
                      const convertedY = absY - offBBox.top;

                      displayAttrs = {
                        ...newAttrs,
                        x: convertedX,
                        y: convertedY,
                        _isInOffCanvasDuringDrag: true,
                      };
                    }
                    // CRITICAL: If dragging over main canvas area but element is on off-canvas stage,
                    // convert the position to be relative to main canvas stage origin
                    else if (targetCanvas === "main" && isOffCanvas && mainBBox && offBBox) {
                      // Element is on off-canvas stage but being dragged over main canvas area
                      // Convert position from off-canvas stage coordinates to main canvas stage coordinates

                      // Step 1: Convert from off-canvas stage coordinates to absolute/client coordinates
                      const absX = offBBox.left + newAttrs.x;
                      const absY = offBBox.top + newAttrs.y;

                      // Step 2: Convert from absolute/client coordinates to main canvas stage coordinates
                      const convertedX = absX - mainBBox.left;
                      const convertedY = absY - mainBBox.top;

                      displayAttrs = {
                        ...newAttrs,
                        x: convertedX,
                        y: convertedY,
                        _isInOffCanvasDuringDrag: false,
                      };
                    }
                    // If element is already on the target canvas, clear/set the flag appropriately
                    else if (targetCanvas === "main" && !isOffCanvas) {
                      // Element is on main canvas and dragging over main canvas - clear flag
                      displayAttrs = {
                        ...newAttrs,
                        _isInOffCanvasDuringDrag: false,
                      };
                    } else if (targetCanvas === "offCanvas" && isOffCanvas) {
                      // Element is on off-canvas and dragging over off-canvas - set flag
                      displayAttrs = {
                        ...newAttrs,
                        _isInOffCanvasDuringDrag: true,
                      };
                    }
                  }
                } catch (error) {
                }
              }

              // Normal individual drag behavior - only if NOT part of a group
              handleDragPreview?.({
                event,
                newAttrs,
                sourceStageRef,
                element: circle,
                type: "Circle",
              });
              // CRITICAL: Do NOT pass _isInOffCanvasDuringDrag into setSelectedObject/setSelectedObjects
              // during drag move. That flag causes findElementCanvas() to return isInOffCanvas: true,
              // which can move the transformer/editor to the off-canvas stage and interrupt Konva's drag
              // (triggering premature dragEnd). Strip the flag so the element stays considered on its
              // current stage until drag end. Same approach as rectangleRenderer and imageRenderer.
              const sourceStage = isOffCanvas ? "offCanvas" : "main";
              const previewTargetStage = targetCanvas ?? sourceStage;
              const selectionBase =
                previewTargetStage !== sourceStage ? newAttrs : displayAttrs;
              const {
                _isInOffCanvasDuringDrag: _unusedFlag,
                ...attrsForSelection
              } = selectionBase;
              const selectionAttrs = {
                ...attrsForSelection,
                type: "Circle",
                maintainAspectRatio: circle.maintainAspectRatio || false,
                _isDragging: true,
                _previewTargetStage: sourceStage,
              };
              setSelectedObject(selectionAttrs);
              setSelectedObjects((prev) => {
                const existingIndex = prev.findIndex(
                  (obj) => obj.id === newAttrs.id && obj.type === "Circle"
                );
                if (existingIndex >= 0) {
                  const updated = [...prev];
                  updated[existingIndex] = selectionAttrs;
                  return updated;
                }
                return prev;
              });
            }
            : undefined
        }
        strokeColor={circle.stroke || strokeColor}
        onTransformStart={
          isInteractive
            ? () => {
              isTransformingRef.current.isTransforming = true;
              // Set ref so stage click that may fire on release (before onTransformEnd) does not clear selection
              if (justFinishedTransformRef) {
                justFinishedTransformRef.current = true;
              }
              if (lastTransformStartTimeRef) {
                lastTransformStartTimeRef.current = Date.now();
              }
              if (selectionBoxRef) selectionBoxRef.current = null;
              if (selectionBoxStageRef) selectionBoxStageRef.current = null;
            }
            : undefined
        }
        onTransform={
          isInteractive
            ? (newAttrs) => {
              // Live-update circle geometry during transform (radius / radiusX / radiusY / rotation)
              // so CircleEditor shows real-time values, matching Rectangle behavior.
              debouncedStateUpdate(() => {
                setCircleState((prev) =>
                  prev.map((c) =>
                    c.id === newAttrs.id ? { ...c, ...newAttrs } : c
                  )
                );
              }, `circle-${newAttrs.id}`);

              // Also keep the selection state in sync so the editor can read updated values
              const selectionAttrs = {
                ...newAttrs,
                type: "Circle",
                maintainAspectRatio: circle.maintainAspectRatio || false,
              };

              setSelectedObject(selectionAttrs);
              setSelectedObjects((prev) => {
                const existingIndex = prev.findIndex(
                  (obj) => obj.id === newAttrs.id && obj.type === "Circle"
                );
                if (existingIndex >= 0) {
                  const updated = [...prev];
                  updated[existingIndex] = selectionAttrs;
                  return updated;
                }
                return prev;
              });
            }
            : undefined
        }
        onClick={
          isInteractive
            ? (e) => {
              if (!isPrimaryMouseButton(e.evt)) {
                return;
              }
              // Disable selection if a tool is locked
              if (lockedAction) {
                return;
              }

              // When multi-select is on and selection is from the other stage, prevent clicking on this stage's elements
              if (isOffCanvas && blockClicksOnOffCanvasElements) {
                e?.evt?.preventDefault?.();
                e?.evt?.stopPropagation?.();
                return;
              }
              if (!isOffCanvas && blockClicksOnMainStageElements) {
                e?.evt?.preventDefault?.();
                e?.evt?.stopPropagation?.();
                return;
              }

              // 🚫 CRITICAL: Check for cross-canvas selection BEFORE setting selectedObject
              // Build list of all currently selected elements
              const allSelectedElements = [];
              if (selectedObjects && selectedObjects.length > 0) {
                selectedObjects.forEach((obj) => {
                  if (obj && obj.id && obj.type) {
                    allSelectedElements.push(obj);
                  }
                });
              }
              if (selectedObject && selectedObject.id && selectedObject.type) {
                const alreadyIncluded = allSelectedElements.some((obj) => obj.id === selectedObject.id);
                if (!alreadyIncluded) {
                  allSelectedElements.push(selectedObject);
                }
              }

              // Helper to check if an element is in main canvas
              const isElementInMainCanvas = (elementId, elementType) => {
                if (!elementId || !elementType) return false;
                switch (elementType) {
                  case "Rect":
                    return rectangles.some((r) => r.id === elementId);
                  case "Circle":
                    return circles.some((c) => c.id === elementId);
                  case "Image":
                    return images.some((i) => i.id === elementId);
                  case "Text":
                    return texts.some((t) => t.id === elementId);
                  case "Arrow":
                    // Check arrows array first
                    if (arrows.some((a) => a.id === elementId)) return true;
                    // Also check lines array for unified arrow elements (with arrow heads)
                    return lines.some((l) => {
                      if (l.id !== elementId) return false;
                      const hasStartArrow =
                        l.hasStartArrow === true || l.pointerAtBeginning === true;
                      const hasEndArrow =
                        l.hasEndArrow === true || l.pointerAtEnd === true;
                      return hasStartArrow || hasEndArrow;
                    });
                  case "Line":
                    // Lines without arrow heads are in the lines array
                    return lines.some((l) => {
                      if (l.id !== elementId) return false;
                      // Only return lines without arrow heads
                      return (
                        !l.hasStartArrow &&
                        !l.hasEndArrow &&
                        !l.pointerAtBeginning &&
                        !l.pointerAtEnd
                      );
                    });
                  case "DoubleSidedArrow":
                    // Check doubleSidedArrows array first
                    if (doubleSidedArrows.some((d) => d.id === elementId)) return true;
                    // Also check lines array for unified double-sided arrow elements
                    return lines.some((l) => {
                      if (l.id !== elementId) return false;
                      const hasStartArrow =
                        l.hasStartArrow === true || l.pointerAtBeginning === true;
                      const hasEndArrow =
                        l.hasEndArrow === true || l.pointerAtEnd === true;
                      return hasStartArrow && hasEndArrow;
                    });
                  default:
                    return false;
                }
              };

              // If we have selected elements, check for cross-canvas conflict
              if (allSelectedElements.length > 0) {
                const selectedElementsCanvasTypes = allSelectedElements.map((obj) =>
                  isElementInMainCanvas(obj.id, obj.type)
                );
                const allFromMainCanvas = selectedElementsCanvasTypes.every((isMain) => isMain === true);
                const allFromOffCanvas = selectedElementsCanvasTypes.every((isMain) => isMain === false);
                const clickedElementIsMainCanvas = !isOffCanvas; // Circle is in main canvas if not isOffCanvas


                // In multi-select mode, prevent cross-canvas selection entirely
                // In normal mode, clear previous selections and continue
                if ((allFromMainCanvas && !clickedElementIsMainCanvas) || (allFromOffCanvas && clickedElementIsMainCanvas)) {
                  if (isMultiSelectMode) {
                    // In multi-select mode, don't allow selecting elements from different stages
                    // Keep existing selections and don't select the new element
                    e?.evt?.stopPropagation?.();
                    e?.evt?.preventDefault?.();
                    return;
                  } else {
                    // Normal mode cross-canvas behavior:
                    // first click clears current selection only.

                    // Clear all current selections
                    setSelectedObject(null);
                    setSelectedObjects([]);
                    setShowTextBox(false);
                    setSelectedText(null);
                    if (setSelectedArrow) {
                      setSelectedArrow(null);
                    }
                    if (setSelectedDoubleSidedArrow) {
                      setSelectedDoubleSidedArrow(null);
                    }
                    if (setSelectedLine) {
                      setSelectedLine(null);
                    }

                    e?.evt?.stopPropagation?.();
                    e?.evt?.preventDefault?.();
                    e.cancelBubble = true;
                    return;
                  }
                }
              }

              const isSameElement =
                selectedObject && selectedObject.id === circle.id;

              const circleAsObj = {
                ...circle,
                type: "Circle",
                fill: circle.fill || circle.fillColor,
                fillColor: circle.fill || circle.fillColor,
                maintainAspectRatio: circle.maintainAspectRatio || false,
              };
              if (isMultiSelectMode) {
                const currentSelectedObjects = selectedObjects || [];
                const isAlreadySelected = currentSelectedObjects.some(
                  (obj) => obj.id === circle.id && obj.type === "Circle",
                );
                if (isAlreadySelected) {
                  cancelActiveGroupDrag?.();
                  const updated = currentSelectedObjects.filter(
                    (obj) => !(obj.id === circle.id && obj.type === "Circle"),
                  );
                  const nextPrimary = updated[0] || null;
                  try {
                    flushSync(() => {
                      setSelectedObjects(updated);
                      setSelectedObject(nextPrimary);
                    });
                  } catch (err) {
                    setSelectedObjects(updated);
                    setSelectedObject(nextPrimary);
                  }
                  let n = e?.target;
                  while (n) {
                    if (typeof n.stopDrag === "function") {
                      try {
                        n.stopDrag();
                      } catch (_) {
                        /* ignore */
                      }
                      break;
                    }
                    n = n.getParent?.() ?? null;
                  }
                  if (e?.evt) {
                    e.evt.stopPropagation();
                    e.evt.preventDefault();
                  }
                  e.cancelBubble = true;
                  return;
                }

                const merged = currentSelectedObjects.some((obj) => obj.id === selectedObject?.id && obj.type === selectedObject?.type)
                  ? [...currentSelectedObjects]
                  : selectedObject
                    ? [...currentSelectedObjects, selectedObject]
                    : [...currentSelectedObjects];
                if (!merged.some((obj) => obj.id === circle.id && obj.type === "Circle")) {
                  merged.push(circleAsObj);
                }
                // CRITICAL: flushSync so selection is committed before stage onClick/effects run; prevents something from overwriting with stale state
                try {
                  flushSync(() => {
                    setSelectedObjects(merged);
                    setSelectedObject(circleAsObj);
                    if (setSelectedArrow) setSelectedArrow(null);
                    if (setSelectedDoubleSidedArrow) setSelectedDoubleSidedArrow(null);
                    if (setSelectedLine) setSelectedLine(null);
                  });
                } catch (e) {
                  setSelectedObjects(merged);
                  setSelectedObject(circleAsObj);
                  if (setSelectedArrow) setSelectedArrow(null);
                  if (setSelectedDoubleSidedArrow) setSelectedDoubleSidedArrow(null);
                  if (setSelectedLine) setSelectedLine(null);
                }
                // Stop propagation so stage onClick never runs and can't overwrite selection
                if (e?.evt) {
                  e.evt.stopPropagation();
                  e.evt.preventDefault();
                }
                e.cancelBubble = true;
                // Do NOT call onClick(e) — the global click handler runs with stale state and can overwrite selectedObjects
                return;
              }
              if (isSameElement) {
                return;
              }
              const selectedObjectIsMainCanvas =
                selectedObject?.id && selectedObject?.type
                  ? isElementInMainCanvas(selectedObject.id, selectedObject.type)
                  : null;
              const isSwitchingBetweenOffCanvasElements =
                !isMultiSelectMode &&
                !!selectedObject?.id &&
                selectedObject.id !== circle.id &&
                selectedObjectIsMainCanvas === false &&
                isOffCanvas === true;
              if (isSwitchingBetweenOffCanvasElements) {
                setSelectedObject(null);
                setSelectedObjects([]);
                setShowTextBox(false);
                setSelectedText(null);
                if (setSelectedArrow) setSelectedArrow(null);
                if (setSelectedDoubleSidedArrow) setSelectedDoubleSidedArrow(null);
                if (setSelectedLine) setSelectedLine(null);
                e?.evt?.stopPropagation?.();
                e?.evt?.preventDefault?.();
                e.cancelBubble = true;
                return;
              }

              setSelectedImage(null);
              setShowTextBox(false);
              setSelectedObject(circleAsObj);

              onClick(e);
            }
            : undefined
        }
        onTransformEnd={(newAttrs) => {
          if (!isInteractive) {
            return;
          }
          // Always clear transform mode so subsequent drags are not blocked
          // when transform end fires after a cross-stage interaction.
          if (isTransformingRef?.current) {
            isTransformingRef.current.isTransforming = false;
          }
          clearDebouncedTransformForKey?.(`circle-${newAttrs.id}`);
          const attrs =
            isOffCanvas && offCanvasStageRef?.current
              ? constrainOffCanvasLiveTransformAttrs(
                  circle,
                  newAttrs,
                  "Circle",
                  offCanvasStageRef.current,
                )
              : newAttrs;
          if (justFinishedTransformRef) {
            justFinishedTransformRef.current = true;
          }
          // Calculate the updated state BEFORE calling setState
          // This ensures saveToHistory receives the correct updated state
          const updatedCircles = isOffCanvas
            ? offCanvasCircles.map((c) =>
              c.id === attrs.id ? { ...c, ...attrs } : c
            )
            : circles.map((c) =>
              c.id === attrs.id ? { ...c, ...attrs } : c
            );

          setCircleState((prev) =>
            prev.map((c) => (c.id === attrs.id ? { ...c, ...attrs } : c))
          );
          setSelectedObject({
            ...attrs,
            type: "Circle",
            maintainAspectRatio: circle.maintainAspectRatio || false,
          });
          setSelectedObjects([
            {
              ...attrs,
              type: "Circle",
              maintainAspectRatio: circle.maintainAspectRatio || false,
            },
          ]);

          // CRITICAL: Mark that there are pending changes so history will be saved on unselection
          // Always set pending changes when transform happens on a selected element
          // This ensures rotation and other transform properties are saved to history
          if (isSelected || isElementBeingEdited) {
            setPendingChanges(true);
            // CRITICAL: Also update the ref to ensure endElementEditing sees the change
            if (pendingChangesRef) {
              pendingChangesRef.current = true;
            }
          }

          // CRITICAL: Don't save to history here - history will be saved when element is unselected
          // This groups all transform changes (width, height, rotation, etc.) into a single history entry
        }}
        onDragEnd={(id, newX, newY, mouseClientX, mouseClientY) => {
          if (!isInteractive) {
            return;
          }
          clearDragPreview?.();

          // CRITICAL: Get the current circle properties from the Konva node, not from stale state
          // The node has the actual current rotation, scaleX, scaleY, etc. that the user sees
          // NOTE: sourceStageRef can be either a ref object (with .current) or the Stage object itself
          let currentCircle = null;
          const stage = sourceStageRef?.current || sourceStageRef; // Handle both ref and direct stage
          if (stage) {
            const node = stage.findOne(`#${id}`);
            if (node) {
              // Get the base circle from state to preserve all other properties
              const baseCircle = (
                isOffCanvas ? offCanvasCircles : circles
              ).find((circle) => circle.id === id);

              if (baseCircle) {
                // Get current rotation from the node
                // The node always has the current visual rotation
                let nodeRotation = node.rotation();
                nodeRotation = nodeRotation % 360;
                if (nodeRotation < 0) nodeRotation += 360;

                // Create currentCircle with properties from node (rotation, scaleX, scaleY, position)
                // CRITICAL: Set rotation, scaleX, scaleY AFTER spreading baseCircle to ensure they override any stale values
                currentCircle = {
                  ...baseCircle,
                  x: newX,
                  y: newY,
                  // CRITICAL: These must come AFTER the spread to override any stale values from baseCircle
                  rotation: nodeRotation, // Use the rotation from node
                  scaleX: node.scaleX(),
                  scaleY: node.scaleY(),
                };
              }
            }
          }

          // Fallback: if we couldn't get the node, use the circle from state
          if (!currentCircle) {
            currentCircle = (isOffCanvas ? offCanvasCircles : circles).find(
              (circle) => circle.id === id
            );
            if (currentCircle) {
              // At least preserve the position update
              currentCircle = {
                ...currentCircle,
                x: newX,
                y: newY,
              };
            }
          }

          // Pass the current circle to handleElementDragEnd so it can preserve all properties
          // The currentCircle has the latest rotation, scaleX, scaleY, etc. from the Konva node
          // CRITICAL: Pass mouseClientX and mouseClientY for group drag detection
          handleElementDragEnd({
            id,
            newX,
            newY,
            type: "Circle",
            sourceStageRef,
            mouseClientX, // Pass mouse coordinates for group drag detection
            mouseClientY, // Pass mouse coordinates for group drag detection
            mainState: circles,
            offCanvasState: offCanvasCircles,
            setMainState: setCircles,
            setOffCanvasState: setOffCanvasCircles,
            setSelectedObjects,
            currentCircle, // Pass current circle with properties from Konva node
          });
        }}
        canvasWidth={stageWidth}
        canvasHeight={stageHeight}
        canvasResizeTrigger={canvasResizeTrigger}
        isOffCanvas={isOffCanvas}
        isPreview={isPreview}
        onCopyToOppositeCanvas={
          isInteractive && handleElementCopy && isOffCanvas
            ? () => handleElementCopy(circle.id, "Circle", "offCanvas")
            : undefined
        }
      />
    );
  };
};
